import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../../components/base/PageLayout';
const DailyRoutine: React.FC = () => {
  React.useEffect(() => {
    document.title = '執事の一日 - プロフェッショナル執事の24時間密着ドキュメント｜執事ベル';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが明かすプロフェッショナル執事の一日の流れ。早朝5時の起床から深夜の就寝準備まで、時間帯別の業務内容と心構えを詳細に解説。主人の起床サポート、スケジュール管理、来客対応、ディナーサービス、翌日の準備など、執事という職業の実態に迫ります。執事を目指す方はもちろん、効率的な時間管理や段取り力を高めたいビジネスパーソンにも参考になる内容です。');
    }
  }, []);


  return (
    <PageLayout hero={<>
<div className="relative h-96 bg-gradient-to-r from-gray-900 to-gray-700 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
            style={{
              backgroundImage: `url('/images/gakken/gk-17.jpg')`
            }}
          />
          <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-serif mb-6 tracking-wide">
              執事の一日
            </h1>
            <p className="text-xl md:text-2xl font-light italic opacity-90">
              A Day in the Life of a Butler
            </p>
          </div>
        </div>
</>}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        

        {/* Article Content */}
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12 p-8 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border-l-4 border-amber-600">
              <p className="text-lg leading-relaxed text-gray-800 mb-0">
                執事の一日は、完璧なサービスを提供するための綿密な準備と実行の連続です。朝の準備から夜の片付けまで、すべての行動には意味があり、お客様の快適さを最優先に考えた時間管理が行われています。プロフェッショナルとしての執事の日常をご紹介いたします。
              </p>
            </div>

            {/* Main Content */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  早朝の準備（5:30-7:00）
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      執事の一日は早朝から始まります。お客様がお目覚めになる前に、すべての準備を整えることが重要です。身だしなみを完璧に整え、その日のスケジュールを確認します。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      天気予報をチェックし、お客様の予定に応じて必要な準備を行います。新聞の配達確認、メールのチェック、緊急事項の有無を確認することから一日が始まります。
                    </p>
                  </div>
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-routine-morning-0c63a8.jpg"
                      alt="早朝の準備"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">早朝のチェックリスト</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-amber-600"></i>
                        <span className="text-gray-700">身だしなみの最終確認</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-amber-600"></i>
                        <span className="text-gray-700">当日のスケジュール確認</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-amber-600"></i>
                        <span className="text-gray-700">天気予報のチェック</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-amber-600"></i>
                        <span className="text-gray-700">新聞・郵便物の確認</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-amber-600"></i>
                        <span className="text-gray-700">緊急連絡事項の確認</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-amber-600"></i>
                        <span className="text-gray-700">館内の安全確認</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-amber-600"></i>
                        <span className="text-gray-700">朝食の準備確認</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-amber-600"></i>
                        <span className="text-gray-700">スタッフとの連携確認</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  朝のサービス（7:00-10:00）
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-routine-service-e93beb.jpg"
                      alt="朝のサービス"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      お客様の起床時間に合わせて、完璧な朝のサービスを提供します。モーニングティーまたはコーヒーの準備、新聞の配置、その日の予定の確認を行います。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      朝食の準備と配膳、お客様のご要望に応じた追加サービスの提供、外出の準備がある場合は適切な服装のアドバイスも行います。
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-lg mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">朝のサービス内容</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-cup-line text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">モーニングサービス</h4>
                      <p className="text-gray-700 text-sm">お好みの飲み物と軽食の提供</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-newspaper-line text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">情報提供</h4>
                      <p className="text-gray-700 text-sm">新聞、メール、重要な連絡事項</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-calendar-line text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">スケジュール管理</h4>
                      <p className="text-gray-700 text-sm">当日の予定確認と調整</p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  日中の業務（10:00-17:00）
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  日中は、お客様の外出中に館内の管理業務を行います。清掃の監督、来客の対応、電話応対、各種手配業務など、多岐にわたる業務を効率的に処理します。
                </p>
                
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      <i className="ri-home-line text-amber-600 mr-2"></i>
                      館内管理業務
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <ul className="space-y-2 text-gray-700">
                        <li>• 清掃スタッフの監督と指導</li>
                        <li>• 館内設備の点検と管理</li>
                        <li>• セキュリティシステムの確認</li>
                        <li>• 備品の在庫管理と発注</li>
                      </ul>
                      <ul className="space-y-2 text-gray-700">
                        <li>• 庭園の管理監督</li>
                        <li>• 車両の管理とメンテナンス</li>
                        <li>• 各種業者との連絡調整</li>
                        <li>• 緊急事態への対応準備</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      <i className="ri-customer-service-line text-amber-600 mr-2"></i>
                      来客・連絡対応
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <ul className="space-y-2 text-gray-700">
                        <li>• 来客の受付と案内</li>
                        <li>• 電話応対と伝言管理</li>
                        <li>• 郵便物の整理と管理</li>
                        <li>• 予約や手配業務</li>
                      </ul>
                      <ul className="space-y-2 text-gray-700">
                        <li>• 重要な連絡事項の整理</li>
                        <li>• 緊急時の連絡体制確認</li>
                        <li>• 各種サービス業者との調整</li>
                        <li>• お客様への報告準備</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  夕方のサービス（17:00-20:00）
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      お客様のご帰宅に合わせて、夕方のサービスを準備します。一日の疲れを癒やすためのアフタヌーンティーやカクテルの準備、夕食の手配を行います。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      その日の出来事の報告、翌日の予定の確認、必要に応じて衣服の準備や各種手配を行います。お客様がリラックスできる環境を整えることが重要です。
                    </p>
                  </div>
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-routine-evening-5a4abb.jpg"
                      alt="夕方のサービス"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                  <h3 className="text-lg font-semibold text-blue-800 mb-4">
                    <i className="ri-moon-line mr-2"></i>
                    夕方のサービス項目
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <ul className="space-y-2 text-blue-700">
                      <li>• お帰りのお出迎え</li>
                      <li>• アフタヌーンティーの準備</li>
                      <li>• 一日の報告とご相談</li>
                      <li>• 夕食の準備と手配</li>
                    </ul>
                    <ul className="space-y-2 text-blue-700">
                      <li>• 翌日の予定確認</li>
                      <li>• 必要な手配の実施</li>
                      <li>• リラックス環境の整備</li>
                      <li>• 夜間の準備</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  夜間の業務（20:00-22:00）
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  夜間は、翌日の準備と館内の最終確認を行います。お客様がお休みになる前の最後のサービスを提供し、安全で快適な夜をお過ごしいただけるよう配慮します。
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-shield-check-line text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">セキュリティ確認</h3>
                    <p className="text-gray-700 text-sm">館内の安全確認と警備システムの作動確認</p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-calendar-todo-line text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">翌日準備</h3>
                    <p className="text-gray-700 text-sm">翌日のスケジュール確認と必要な準備</p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-file-text-line text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">記録整理</h3>
                    <p className="text-gray-700 text-sm">一日の業務記録と重要事項の整理</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  プロフェッショナルとしての心構え
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  執事の一日は、単なる業務の連続ではありません。すべての行動が、お客様の快適さと満足度を高めるための意図的な選択なのです。常に学び続け、改善を重ねる姿勢が重要です。
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-amber-800 mb-3">
                    <i className="ri-lightbulb-line mr-2"></i>
                    執事ベルからのアドバイス
                  </h3>
                  <p className="text-amber-700 leading-relaxed">
                    「執事の一日は、完璧なサービスを提供するための準備と実行の連続です。しかし、最も重要なのは、お客様への真心からの思いやりです。技術や知識は身につけることができますが、相手を思う心は、日々の実践の中で育まれるものです。毎日を丁寧に過ごし、常に向上を目指す。それが真のプロフェッショナルの姿なのです。」
                  </p>
                </div>
              </section>
            </div>

            {/* Related Articles */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h3 className="text-2xl font-serif text-gray-900 mb-8">関連記事</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <a href="/wisdom/articles/time-management" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-time-e31d3d.jpg"
                    alt="時間管理の技術"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">時間管理の技術</h4>
                    <p className="text-sm text-gray-600 mt-2">効率的で品格ある時間の使い方</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/organization" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-org-86aa31.jpg"
                    alt="整理整頓の哲学"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">整理整頓の哲学</h4>
                    <p className="text-sm text-gray-600 mt-2">美しく機能的な空間作りの秘訣</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/observation" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-obs2-17b992.jpg"
                    alt="観察力の磨き方"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">観察力の磨き方</h4>
                    <p className="text-sm text-gray-600 mt-2">お客様のニーズを察知する技術</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default DailyRoutine;
